import React from 'react';
import { Team, SportType } from '@/types';
import { resolveNationalTeamFlagEmoji } from '@/utils/teamFlags';
import { resolveTeamColors } from '@/utils/teamColors';

interface TeamLogoProps {
  team: Team;
  sportType: SportType;
  size?: 'sm' | 'md' | 'lg';
}

const TeamLogo: React.FC<TeamLogoProps> = ({ team, sportType, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };

  const flagContainerClasses = {
    sm: 'w-7 h-7',
    md: 'w-9 h-9',
    lg: 'w-11 h-11',
  };

  const flagSizeClasses = {
    sm: 'text-lg scale-[1.18]',
    md: 'text-[1.6rem] scale-[1.25]',
    lg: 'text-[2rem] scale-[1.32]',
  };
  
  // Get default sport-specific background color if primaryColor is not defined
  const getDefaultBgColor = () => {
    switch (sportType) {
      case 'nba':
        return 'bg-orange-600';
      case 'nfl':
        return 'bg-amber-700';
      case 'mlb':
        return 'bg-blue-700';
      case 'soccer':
        return 'bg-green-700';
      case 'nhl':
        return 'bg-cyan-700';
      default:
        return 'bg-gray-700';
    }
  };

  const nationalTeamFlag = resolveNationalTeamFlagEmoji(team, sportType);
  if (nationalTeamFlag) {
    return (
      <div
        className={`${flagContainerClasses[size]} rounded-full bg-white/95 ring-1 ring-slate-300/70 flex items-center justify-center flex-shrink-0 overflow-hidden shadow-[0_10px_24px_rgba(15,23,42,0.18)]`}
        role="img"
        aria-label={`${team.name} flag`}
        title={team.name}
      >
        <span className={`${flagSizeClasses[size]} leading-none select-none`}>{nationalTeamFlag}</span>
      </div>
    );
  }
  
  // Get team colors directly from the team object, with soccer placeholder colors resolved.
  const teamWithResolvedColors = resolveTeamColors(team, sportType);
  const teamColor = teamWithResolvedColors.primaryColor || '';
  const textColor = teamWithResolvedColors.secondaryColor || '#FFFFFF'; // Default to white text if no secondary color
  
  // For white backgrounds, add a border to make it visible
  const extraStyle = teamColor.toUpperCase() === '#FFFFFF' ? 
    { backgroundColor: teamColor, border: '1px solid #E2E8F0' } : 
    { backgroundColor: teamColor };
  
  // Fallback to abbreviation in colored circle
  return (
    <div 
      className={`${sizeClasses[size]} ${!teamColor ? getDefaultBgColor() : ''} rounded-full flex items-center justify-center flex-shrink-0`}
      style={teamColor ? extraStyle : {}}
    >
      <span 
        className="font-bold" 
        style={{ color: textColor }}
      >
        {team.abbreviation}
      </span>
    </div>
  );
};

export default TeamLogo;
